import type {ThreadUserMessagePart} from '@assistant-ui/core';
import {useAui, useAuiState} from '@assistant-ui/react';
import {useCallback} from 'react';

import {toAUIContentParts} from '../../chat/messageConversions';
import {
  selectActiveThread,
  useActiveThread,
  useActiveThreadActiveNetworkDomains,
  useAgentChatStore,
} from '../../chat/store';
import {
  CONTINUATIONS_BY_TOOL_NAME,
  resolvePendingToolInteraction,
} from '../../chat/tools/catalog';
import type {
  ContinuationSpec,
  PendingPayload,
} from '../../chat/tools/continuationSpec';
import {useAgentChatReadonly} from '../../chat/useAgentChatReadonly';
import {useTrackWBAgentComposer} from '../useTrackWBAgent';

export interface ToolInteraction {
  /** isActionable defines whether this tool call is awaiting a response. */
  isActionable: boolean;
  /** isDisabled is whether submission is currently disabled. */
  isDisabled: boolean;
  /** payload is the structured request for the interaction, when actionable. */
  payload?: PendingPayload;
  /** submit sends the tool's result as the next-turn continuation. */
  submit: (result: unknown) => void;
}

/**
 * useToolInteraction manages execution for HITL tool renderers. Given a tool
 * call's id, it reports whether that call is the actionable one and exposes
 * `submit`, which turns the tool's structured result into the next user turn
 * via the tool's continuation spec.
 *
 * NOTE: This departs from AUI's native `addResult`/`onAddToolResult` HITL flow
 * because the backend has no tool-result endpoint; a resolved call can only
 * continue as a new child turn.
 */
export function useToolInteraction(toolCallId: string): ToolInteraction {
  const aui = useAui();
  const isRunning = useAuiState(s => s.thread.isRunning);
  const isReadonly = useAgentChatReadonly();
  const activeThread = useActiveThread();
  const activeNetworkDomains = useActiveThreadActiveNetworkDomains();
  const store = useAgentChatStore();
  const {trackMessageSent} = useTrackWBAgentComposer();

  const interaction = resolvePendingToolInteraction(activeThread);
  const isActionable =
    interaction != null && interaction.toolCallId === toolCallId;

  const submit = useCallback(
    (result: unknown) => {
      if (isReadonly || isRunning) {
        return;
      }

      // Re-derive at submit time whether this call is still actionable, so a
      // second click always sees the latest state rather than one guarded by
      // a flag that could get stuck.
      const current = resolvePendingToolInteraction(
        selectActiveThread(store.getState())
      );
      if (current == null || current.toolCallId !== toolCallId) {
        return;
      }

      const spec = CONTINUATIONS_BY_TOOL_NAME[current.toolName] as
        | ContinuationSpec<unknown>
        | undefined;
      if (!spec) {
        return;
      }

      const {parts, permissions} = spec.buildContinuation(
        current.payload,
        spec.parseResult(result),
        {activeNetworkDomains}
      );
      const content = parts.flatMap(
        toAUIContentParts
      ) as ThreadUserMessagePart[];
      // NOTE: A child turn, so it inherits the asking turn's agent config.
      // NOTE: Bypasses the queue, which would drop `toolContinuation` and
      // leave the response blocked by the send guard.
      aui.thread().append({
        role: 'user',
        content,
        runConfig: {
          custom: {
            ...(permissions ? {permissions} : {}),
            toolContinuation: true,
            shouldBypassQueue: true,
          },
        },
      });
      trackMessageSent('tool_ui');
    },
    [
      aui,
      isReadonly,
      isRunning,
      store,
      activeNetworkDomains,
      toolCallId,
      trackMessageSent,
    ]
  );

  return {
    isActionable,
    isDisabled: isRunning || isReadonly,
    payload: isActionable ? interaction?.payload : undefined,
    submit,
  };
}
